import { find, isEmpty, isUndefined, map } from "lodash";
import React from "react";
import { CellRenderers, columnRenderArrayData } from "utils/tableUtils";

import GenericPullConfigListing from "../GenericPullConfigListing";
import { renderLinks } from "../twistlock/TwistLockConfigListing";
import PrismaCloudComputeCredentialsForm from "./PrismaCloudComputeCredentialsForm";
import PrismaCloudComputeMappingForm from "./PrismaCloudComputeMappingForm";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { parseAndCleanVersions, PRISMA_CLOUD_COMPUTE_RADIO_OPTIONS } from "./PrismaCloudComputeMappingUtils";

const PrismaCloudComputeConfligListing = (props) => {
  const featureFlags = useFeatureFlag();
  const extraColumns = [
    {
      title: "Type",
      dataIndex: "type",
      width: 200,
      render: (type) =>
        find(PRISMA_CLOUD_COMPUTE_RADIO_OPTIONS, { value: type })?.label ??
        type,
    },
    {
      title: "Cluster",
      dataIndex: "clusterWithImages",
      width: 240,
      render: (clusterWithImages) => {
        if (clusterWithImages === "all") {
          return "All";
        }
        if (isEmpty(clusterWithImages)) {
          return CellRenderers.VALUE_OR_NA();
        }
        const clusters = parseClusterWithImages(clusterWithImages).clusters;
        return map(clusters, (clusterString) =>
          isEmpty(clusterString)
            ? columnRenderArrayData(CellRenderers.VALUE_OR_NA())
            : columnRenderArrayData(clusterString),
        );
      },
      filterLabelFormatter: (val) => (val === "all" ? "All" : val),
    },
    {
      title: "Image",
      dataIndex: "clusterWithImages",
      width: 240,
      render: (clusterWithImages, record) => {
        if (clusterWithImages === "all") {
          return "All";
        }
        if (isEmpty(clusterWithImages)) {
          return CellRenderers.VALUE_OR_NA();
        }
        const images = parseClusterWithImages(clusterWithImages).images;
        return map(images, (imageString) =>
          !isEmpty(imageString)
            ? renderLinks(imageString, record, "images/deployed", props.name)
            : columnRenderArrayData("All"),
        );
      },
      filterLabelFormatter: (val) => (val === "all" ? "All" : val),
    },
    {
      title: "Registry",
      dataIndex: "registryName",
      width: 240,
      render: (registryName, record) => {
        if (registryName === "all") {
          return "All";
        }
        return renderLinks(
          registryName,
          record,
          "images/registries",
          props.name,
        );
      },
      filterLabelFormatter: (val) => (val === "all" ? "All" : val),
    },
    {
      title: "CI",
      dataIndex: "ciImageName",
      width: 240,
      render: (ciImageName, record) => {
        if (ciImageName === "all") {
          return "All";
        }
        return renderLinks(ciImageName, record, "images/ci", props.name);
      },
      filterLabelFormatter: (val) => (val === "all" ? "All" : val),
    },
    ...(featureFlags.PRISMA_COMPUTE_ENABLE_IMAGE_VERSION_MAPPING ? [{
      title: "Versions",
      dataIndex: "versions",
      width: 240,
      render: (text, record) => {
        const { 
          deployedImagesWithVersions, 
          registryImagesWithVersions, 
          ciImagesWithVersions,
          ciImageName,
          registryName,
          type,
        } = record;

        if(type !== "images") {
          return CellRenderers.VALUE_OR_NA();
        }
        
        const allEntries = [];

        parseAndCleanVersions(deployedImagesWithVersions).forEach(d => {
          const versionDisplay = d.versions === CellRenderers.VALUE_OR_NA() ? "All" : d.versions;
          allEntries.push(`Deployed - ${d.repo} - ${versionDisplay}`);
        });

        parseAndCleanVersions(registryImagesWithVersions).forEach(r => {
          allEntries.push(`Registry - ${r.repo} - ${r.versions}`);
        });

        parseAndCleanVersions(ciImagesWithVersions).forEach(c => {
          allEntries.push(`CI - ${c.repo} - ${c.versions}`);
        });

        if (isEmpty(allEntries)) {
          if(!isEmpty(ciImageName) || !isEmpty(registryName)) {
          return CellRenderers.VALUE_OR_NA();
          } else {
            return "All"
          }
        }
        
        return columnRenderArrayData(allEntries.join(','));
      }
    }] : []),
    {
      title: "Application Security",
      dataIndex: "codeRepoName",
      width: 240,
      render: (codeRepoName, record) =>
        codeRepoName === "All"
          ? "All"
          : renderLinks(codeRepoName, record, "code-security", props.name),
    },
    {
      title: "Host Cluster",
      dataIndex: "hostClusterName",
      width: 240,
      render: (hostCluster, record) =>
        !hostCluster && record.hostName
          ? "Not mapped with cluster"
          : hostCluster === "ALL-CLUSTERS"
            ? "All"
            : columnRenderArrayData(hostCluster),
    },
    {
      title: "Host",
      dataIndex: "hostName",
      width: 240,
      render: (hostName, record) => {
        return hostName === "ALL-HOSTS"
          ? "All"
          : renderLinks(hostName, record, "hosts/deployed", props.name);
      },
      filterLabelFormatter: (val) => (val === "ALL-HOSTS" ? "All" : val),
    },
    {
      title: "API Security Config",
      dataIndex: "isApiSecurityConfig",
      width: 150,
      render: (isApiSecurityConfig) => isUndefined(isApiSecurityConfig) ? CellRenderers.VALUE_OR_NA() : isApiSecurityConfig === "true" ? "Enabled" : "Disabled",
    }
  ];

  return (
    <GenericPullConfigListing
      {...props}
      toolName={props.name}
      MappingForm={PrismaCloudComputeMappingForm}
      extraColumns={extraColumns}
      CredentialsForm={PrismaCloudComputeCredentialsForm}
    />
  );
};

export default PrismaCloudComputeConfligListing;

function parseClusterWithImages(clusterWithImages) {
  const separator = "|:";
  if (!clusterWithImages) {
    return { clusters: [], images: [] };
  }

  const clusters = [];
  const images = [];

  clusterWithImages
    .split(",")
    .map((seg) => seg.trim())
    .forEach((seg) => {
      const [rawCluster, rawImages = ""] = seg.split(separator);

      // Normalize cluster: empty when nothing before '|:'; "All" when rawCluster === 'all'
      const cluster =
        String(rawCluster).toLowerCase() === "all" ? "All" : rawCluster || "";

      clusters.push(cluster);

      if (cluster === "All") {
        // display "All" in images too
        images.push("All");
      } else {
        // split into an array of image strings
        const imgs = rawImages
          .replace(/;$/, "") // drop ;
          .split(";")
          .filter(Boolean);
        images.push(imgs);
      }
    });

  return { clusters, images };
}

/**
 * Input: "repo1|:v1;v2,repo2|:v3"
 * Output: [
 * { repo: "repo1", versions: "v1; v2" },
 * { repo: "repo2", versions: "v3" }
 * ]
 */
function parseRepoVersionString(str) {
  if (!str || str === "all") {
    return [];
  }
  return str.split(',').map(segment => {
  const [repo, versionsPart] = segment.split("|:");
  const versions = (versionsPart || "").split(';').filter(Boolean).join(', ');
  return { repo, versions: versions || CellRenderers.VALUE_OR_NA() }; // Handle empty versions
  });
}